import {boundMethod} from "autobind-decorator";
import React from "react";
import {IntlContext} from "react-intl";

import {ILocalizedText} from "@translate/models";
import {EDocIdPrefixes, IItemSelectionState, INestedBaseItem} from "../models";
import {ISample} from "./models";

import T, {intl2Str, parseNumber} from "@translate/T";
import {getDocIdInfo} from "../doc-type";
import {getSelection, isSelected} from "../SearchItem";
import DirectGroup from "./DirectGroup";

export interface ISpocSamplesProps {
    project: number;
    selection: IItemSelectionState;
    items: INestedBaseItem[] | undefined;
    query: string;

    onChange(selection: IItemSelectionState): void;
}

class SpocSamples extends React.PureComponent<ISpocSamplesProps> {
    private get samples(): null | ISample[] {
        const {project, selection, items} = this.props;
        const spocItems = items?.filter((x) =>
            x.id.startsWith(EDocIdPrefixes.SpocSamples),
        );

        if (!spocItems?.length) {
            return null;
        }

        return spocItems.map((x) => {
            const {url, id} = getDocIdInfo(x.id, project);
            const [_, __, type, series, repetition, started] = x.id.split("/");
            const {hasAttachment, name, prefixName} = x;

            return {
                id,
                url,
                type,
                name,
                prefixName,
                started,
                series: parseNumber(series),
                repetition: parseNumber(repetition),
                selected: isSelected(id, selection),
                hasAttachment,
            };
        });
    }

    @boundMethod
    public onSelected(checked: boolean, id: string | string[]) {
        const samples = this.samples;
        if (!samples) {
            return;
        }

        let targetIds = id;
        if (!Array.isArray(targetIds)) {
            targetIds = [targetIds];
        }

        for (const targetId of targetIds) {
            for (const sample of samples) {
                if (targetId.includes(sample.id)) {
                    sample.selected = checked;
                }
            }
        }

        this.props.onChange(getSelection(samples));
    }

    public render() {
        const samples = this.samples;
        if (!samples) {
            return null;
        }

        return (
            <React.Fragment>
                {this.renderBlanks(samples)}
                {this.renderSamples(samples)}
            </React.Fragment>
        );
    }

    private renderBlanks(samples: ISample[]) {
        const {query} = this.props;
        const blanks = samples.filter(
            (x) => x.series === 0 && x.repetition === 0,
        );

        if (!blanks.length) {
            return null;
        }

        return (
            <DirectGroup
                samples={blanks}
                query={query}
                toggle={this.onSelected}
            >
                <T>Blank</T>
            </DirectGroup>
        );
    }

    private renderSamples(samples: ISample[]) {
        const {query} = this.props;
        const allSeries = samples.filter((x) => x.series > 0);

        if (!allSeries.length) {
            return null;
        }

        const seriess: ISample[][] = [];
        for (const serie of allSeries) {
            // if the last entry has diffrent series, start new array entry
            if (
                !seriess.length ||
                seriess[seriess.length - 1][0].series !== serie.series
            ) {
                seriess.push([serie]);
            } else {
                seriess[seriess.length - 1].push(serie);
            }
        }

        if (seriess.length === 1) {
            return (
                <DirectGroup
                    samples={seriess[0]}
                    query={query}
                    toggle={this.onSelected}
                >
                    <T>Sample</T>
                </DirectGroup>
            );
        }

        return seriess.map((series, i) => {
            const id = series[0].prefixName?.split(".")[0]; // not perfect, but i can live with it
            const text: ILocalizedText = (intl) =>
                intl2Str(intl, "Sample {series}", {series: id});

            return (
                <DirectGroup
                    key={id}
                    samples={series}
                    query={query}
                    toggle={this.onSelected}
                >
                    <IntlContext.Consumer children={text} />
                </DirectGroup>
            );
        });
    }
}

export default SpocSamples;
